import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type { AuditEntry, AuditPage } from './audit';

const PAGE_SIZE = 50;

export interface AuditQuery {
    from: string;
    to: string;
    action?: string;
    actorId?: string;
    locationId?: string;
    restaurantId?: string;
}

interface Slot {
    key: string;
    rows: AuditEntry[];
    cursor: string | null;
    failed: boolean;
}

/** First page for `query`, then "load more" with the cursor. `query === null` waits (state "loading"). */
export const useAuditLog = (query: AuditQuery | null) => {
    const key = query ? JSON.stringify(query) : '';
    const [slot, setSlot] = useState<Slot>({ key: '', rows: [], cursor: null, failed: false });
    const [more, setMore] = useState<'idle' | 'loading' | 'error'>('idle');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!key) return;
        let live = true;
        setMore('idle');
        api.get<AuditPage>('/audit-log', { params: { ...JSON.parse(key), limit: PAGE_SIZE } }).then(
            ({ data }) => live && setSlot({ key, rows: data.items, cursor: data.nextCursor, failed: false }),
            () => live && setSlot({ key, rows: [], cursor: null, failed: true })
        );
        return () => {
            live = false;
        };
    }, [key, attempt]);

    const current = slot.key === key && key !== '';

    const loadMore = useCallback(() => {
        if (!current || !slot.cursor || more === 'loading') return;
        setMore('loading');
        api.get<AuditPage>('/audit-log', { params: { ...JSON.parse(key), limit: PAGE_SIZE, cursor: slot.cursor } }).then(
            ({ data }) => {
                setSlot((s) => (s.key === key ? { ...s, rows: [...s.rows, ...data.items], cursor: data.nextCursor } : s));
                setMore('idle');
            },
            () => setMore('error')
        );
    }, [current, slot.cursor, more, key]);

    const reload = useCallback(() => {
        setSlot({ key: '', rows: [], cursor: null, failed: false });
        setAttempt((n) => n + 1);
    }, []);

    return {
        rows: current ? slot.rows : [],
        state: !current ? ('loading' as const) : slot.failed ? ('error' as const) : ('ready' as const),
        hasMore: current && slot.cursor !== null,
        more,
        loadMore,
        reload,
    };
};
